import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
} from "@angular/core";
import { BehaviorSubject, Observable, Observer } from "rxjs";
import { Ticket } from "../../../model/ticket";
import { TicketService } from "../../../services/ticket.service";
import { AuthService } from "../../../auth.service";
import { Router } from "@angular/router";
import { NavigationHelper } from "../../../services/navigation.helper";
import { BranchService } from "../../../services/branch.service";
import { Navigation } from "../../../model/navigation";
import { SingleChoice } from "../../../model/singlechoice";
import { Labeler } from "../../../model/labeler";
import { MultiActionService } from "../../../services/multi-action.service";

@Component({
    selector: "ticket-by-branch",
    templateUrl: "./ticket-by-branch.component.html",
    styleUrls: ["./ticket-by-branch.component.css"],
})
export class TicketByBranchComponent implements OnInit {
    @Input() isMobile = false;
    @Output() ticketSelected = new EventEmitter<number>();
    @Output() createNewTicket = new EventEmitter<void>();

    branchIds: number[] = [];
    branchTickets = new Map<number, Ticket[]>();
    branchPages = new Map<number, number>();
    branchTotalPages = new Map<number, number>();
    branchAllIds = new Map<number, number[]>();
    activeBranchIndex: number | null = null;
    selectedTicket: Ticket | null = null;
    pageSize = 10;

    constructor(
        public ticketService: TicketService,
        private auth: AuthService,
        private router: Router,
        private nav: NavigationHelper,
        private branchService: BranchService,
        private multiActionService: MultiActionService,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        const token = this.auth.token;

        this.ticketService.resultsByBranch.subscribe((branchMap) => {
            this.branchIds = Array.from(branchMap.keys());
            this.branchAllIds = branchMap;
            this.branchTickets.clear();
            this.branchPages.clear();
            this.branchTotalPages.clear();

            const nav = this.nav.freshNavigation;
            if (nav.id === "by-branch" && nav.action) {
                const urlBranchId = parseInt(nav.action, 10);
                const idx = this.branchIds.indexOf(urlBranchId);
                this.activeBranchIndex = idx >= 0 ? idx : null;
                if (idx >= 0) {
                    const page = (nav.fragmentTopic === "page" && nav.fragmentId)
                        ? parseInt(nav.fragmentId, 10)
                        : 0;
                    this.loadBranchPage(urlBranchId, page);
                }
            } else {
                this.activeBranchIndex = null;
            }

            for (const branchId of this.branchIds) {
                const ids = branchMap.get(branchId) || [];
                this.branchTotalPages.set(
                    branchId,
                    Math.ceil(ids.length / this.pageSize)
                );
                this.branchPages.set(branchId, 0);
            }
            this.cdr.detectChanges();
        });

        this.ticketService.searchByBranch(token).subscribe();
    }

    branchName(branchId: number): string {
        const branchLabel = this.branchService.labelForId(branchId);
        const branch = this.branchService.branchesData.find((branch) => branch.id === branchId);

        const abbr = branchLabel || "";
        const name = branch ? branch.name : "";
        return `${abbr}, ${name}`.trim();
    }

    branchTicketCount(branchId: number): number {
        return (this.branchAllIds.get(branchId) || []).length;
    }

    onTabOpen(event: any): void {
        const branchId = this.branchIds[event.index];
        this.activeBranchIndex = event.index;
        this.router.navigate([`tickets/by-branch/${branchId}`]);
        if (!this.branchTickets.has(branchId)) {
            this.loadBranchPage(branchId, 0);
        }
    }

    onTabClose(): void {
        this.activeBranchIndex = null;
    }

    loadBranchPage(branchId: number, page: number): void {
        const allIds = this.branchAllIds.get(branchId) || [];
        const start = page * this.pageSize;
        const end = Math.min(start + this.pageSize, allIds.length);
        const pageIds = allIds.slice(start, end);

        if (pageIds.length === 0) return;

        const token = this.auth.token;
        this.ticketService.fetchTickets(token, pageIds).subscribe((tickets) => {
            this.branchTickets.set(branchId, tickets);
            this.branchPages.set(branchId, page);
            this.cdr.detectChanges();
        });
    }

    getTickets(branchId: number): Ticket[] {
        return this.branchTickets.get(branchId) || [];
    }

    getCurrentPage(branchId: number): number {
        return this.branchPages.get(branchId) || 0;
    }

    getTotalPages(branchId: number): number {
        return this.branchTotalPages.get(branchId) || 0;
    }

    goToPage(branchId: number, page: number): void {
        const total = this.getTotalPages(branchId);
        if (page < 0 || page >= total) return;
        this.loadBranchPage(branchId, page);
        const h = this.nav.history;
        if (h.length > 0 && /by-branch/.test(h[h.length - 1])) {
            h.splice(h.length - 1, 1);
        }
        const path = page > 0
            ? `tickets/by-branch/${branchId}/page/${page}`
            : `tickets/by-branch/${branchId}`;
        this.router.navigate([path]);
    }

    select(ticket: Ticket): void {
        this.selectedTicket = ticket;
        if (ticket && ticket.id) {
            this.ticketService.select(ticket, true);
            this.ticketSelected.emit(ticket.id!);
            this.router
                .navigate([`tickets/${ticket.id}/fragmentedit/comment/-9`])
                .then(() => {
                    this.nav.freshNavigation;
                });
        }
    }

    toggleSelect(ticket: Ticket): void {
        this.ticketService.toggleTicketSelection(ticket);
    }

    isSelected(ticket: Ticket): boolean {
        return this.ticketService.isTicketSelected(ticket);
    }

    createTicket(): void {
        this.createNewTicket.emit();
        const newTicket = new Ticket(
            Navigation.NEW,
            "",
            "",
            "",
            0,
            "",
            0,
            0,
            "",
            0,
            0,
            "",
            0,
            new Date(),
            "",
            false
        );
        this.router.navigate([`tickets/${Navigation.NEW}/edit`]).then(() => {
            this.nav.freshNavigation;
            this.ticketService.select(newTicket);
            this.ticketSelected.emit(Navigation.NEW);
        });
    }

    relaySingleChoice(choice: SingleChoice): void {
        this.nav.startChoice(choice);
    }

    get actionLabelerByService(): Labeler {
        return this.multiActionService;
    }

    get actionIdObserver(): Partial<Observer<number>> {
        return {
            next: (id) => {
                this.ticketService.selectMultiActionId(id);
            },
        };
    }

    get actionUrlObserver(): Observable<string> {
        return this.ticketService.selectedMultiActionUri;
    }

    getStartRow(branchId: number): number {
        return this.getCurrentPage(branchId) * this.pageSize;
    }

    getEndRow(branchId: number): number {
        const start = this.getStartRow(branchId);
        const tickets = this.getTickets(branchId);
        return start + tickets.length;
    }

    getPaginationButtons(branchId: number): (number | string)[] {
        const total = this.getTotalPages(branchId);
        if (total <= 10) {
            return Array.from({ length: total }, (_, i) => i);
        }
        const current = this.getCurrentPage(branchId);
        const pages: (number | string)[] = [0, 1, 2];
        if (current > 4) pages.push("...");
        for (let i = Math.max(3, current - 2); i <= Math.min(total - 4, current + 2); i++) {
            pages.push(i);
        }
        if (current < total - 5) pages.push("...");
        pages.push(total - 3, total - 2, total - 1);
        return pages;
    }
}
